import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

export const teamsApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "teamsApi",
    baseUrl: "/api/teams",
  }),
  reducerPath: "teamsApi",
  tagTypes: ["Teams"],
  endpoints: (builder) => ({
    getTeams: builder.query<any, void>({
      query: () => `/`,
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Teams" }],
    }),
    getTeamsByCompanyId: builder.query<
      any,
      {
        companyId: string;
        notIn?: string;
        type?: string;
        include?: string;
        companyOnly?: string;
      }
    >({
      query: ({ companyId, notIn, type, include, companyOnly }) => {
        const params = new URLSearchParams();
        if (notIn) params.append("notIn", notIn);
        if (type) params.append("type", type);
        if (include) params.append("include", include);
        if (companyOnly) params.append("companyOnly", companyOnly);
        const queryString = params.toString();
        return `/company/${companyId}${queryString ? `?${queryString}` : ""}`;
      },
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Teams" }],
    }),
    createTeam: builder.mutation<
      any,
      { companyId: string; teamData: { name: string; type?: string } }
    >({
      query: ({ companyId, teamData }) => ({
        url: `/../../api/companies/${companyId}/team`,
        method: "POST",
        body: teamData,
      }),
      invalidatesTags: [{ type: "Teams" }],
    }),
    updateTeam: builder.mutation<
      any,
      { teamId: string; teamData: { name: string; type?: string } }
    >({
      query: ({ teamId, teamData }) => ({
        url: `/${teamId}`,
        method: "PUT",
        body: { teamData },
      }),
      invalidatesTags: [{ type: "Teams" }],
    }),
    deleteTeam: builder.mutation<any, { companyId: string; teamId: string }>({
      query: ({ companyId, teamId }) => ({
        url: `/../../api/companies/${companyId}/team/${teamId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Teams" }],
    }),
    addTeamMember: builder.mutation<
      any,
      { teamId: string; personId: string; roleId?: string }
    >({
      query: ({ teamId, personId, roleId }) => ({
        url: `/${teamId}/person`,
        method: "POST",
        body: { personId, roleId },
      }),
      invalidatesTags: [{ type: "Teams" }],
    }),
    removeTeamMember: builder.mutation<
      any,
      { teamId: string; personId: string }
    >({
      query: ({ teamId, personId }) => ({
        url: `/${teamId}/person/${personId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Teams" }],
    }),
    getTeamsForPerson: builder.query<
      any,
      { personId: string; companyId: string }
    >({
      query: ({ personId, companyId }) =>
        `/person/${personId}/company/${companyId}`,
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Teams" }],
    }),
  }),
});

export const {
  useGetTeamsQuery,
  useGetTeamsByCompanyIdQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useAddTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useGetTeamsForPersonQuery,
} = teamsApi;
